import { Component, ElementRef, inject, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Match } from '../_shared/models/match.model';
import { Point } from '../_shared/models/point.model';
import { Set } from '../_shared/models/set.model';
import { SideStats } from '../_shared/models/sideStats.model';
import { StatsFilter } from '../_shared/models/statsFilters.model';
import { BoardHelpService } from '../match-tracker/tracker-board/board-help.service';
import { ScoreFormat } from '../_shared/models/score-format.model';
import { TieDetailsService } from '../ties/tie-details/tie-details.service';
import { Tie } from '../_shared/models/tie.model';
import { ScoreboardService } from '../scoreboard/scoreboard.service';
import * as hexRgb from 'hex-rgb';
import { ShotOutcomeEnum } from '../_shared/models/csTypes/enums/shot_outcome';
import { StatsService } from './stats.service';
import { DownloadCsvService } from '../_core/services/download-csv.service';
import { AuthService } from '../auth/auth.service';

@Component({
  standalone: false,
    selector: 'app-stats',
    templateUrl: './stats.page.html',
    styleUrls: ['./stats.page.scss']
})

export class StatsPage implements OnInit, OnChanges {
    authService = inject(AuthService);

    @Input() match: Match;
    @ViewChild('progress1') progress1: ElementRef;
    stats: any;
    screenHeight: number;

    side1Stats = new SideStats();
    side2Stats = new SideStats();
    filters = new StatsFilter();
    noStats = 0;
    selectedSet = 0;
    scoreFormat: ScoreFormat;
    tie: Tie;
    side1Color: string;
    side1Progress: string;
    side2Color: string;
    side2Progress: string;
    rowStyle: string;
    participantSide1: string;
    participantSide2: string;

    constructor(
        private platform: Platform,
        private bhs: BoardHelpService,
        private tieDetailsService: TieDetailsService,
        private scoreboardService: ScoreboardService,
        private statsService: StatsService,
        private downloadCsvService: DownloadCsvService
    ) { }

    ngOnChanges(change: SimpleChanges) {
        this.match = change.match.currentValue;
        this.scoreFormat = this.bhs.getScoreFormat(this.match.matchFormat, this.match.discipline);
        this.noStats = 0;
        this.getStats();
    }

    async ngOnInit() {
        if (this.match.tie) {
            this.tie = await this.tieDetailsService.getTieInfo(this.match.tie, false, false, 'stats ln 63');
        }

        // Get colors for each side
        this.side1Color = this.scoreboardService.getColor(1, this.tie);
        this.side2Color = this.scoreboardService.getColor(2, this.tie);

        // Get color used in the background of a progress bar
        this.side1Progress = this.convertArrayToRgba(hexRgb.default(this.side1Color, { format: 'array' }));
        this.side2Progress = this.convertArrayToRgba(hexRgb.default(this.side2Color, { format: 'array' }));
        this.rowStyle = `linear-gradient(to right, ${this.side1Progress} 50%, ${this.side2Progress} 50%)`;

        this.participantSide1 = this.bhs.getPlayersNames(this.match, 1);
        this.participantSide2 = this.bhs.getPlayersNames(this.match, 2);

        this.screenHeight = this.platform.height();
    }

    selectSet(ev) {
        this.selectedSet = Number(ev.detail.value);
        this.getStats();
    }

    private getStats() {
        this.side1Stats = new SideStats();
        this.side2Stats = new SideStats();
        if (this.selectedSet) {
            this.getStatsFromGames(this.match.sets[this.selectedSet - 1]);
        } else {
            this.match.sets?.forEach((set, i) => {
                this.getStatsFromGames(set);
            });
        }
    }

    getStatsFromGames(setObject: Set) {
        setObject.games.forEach((game, j) => {

            // Break points converted
            if (this.statsService.isBreakPointConverted(game, this.scoreFormat, 1)) this.side1Stats.breakPointConverted++;
            if (this.statsService.isBreakPointConverted(game, this.scoreFormat, 2)) this.side2Stats.breakPointConverted++;

            // POINTS
            game.points.forEach((point, k) => {
                if (this.getFilters(point)) {
                    // Count total number of serves played per side
                    if (point.winningSide) {
                        if (point.server?.sideNumber === 1) {
                            this.side1Stats.servePlayed++;
                        } else if (point.server?.sideNumber === 2) {
                            this.side2Stats.servePlayed++;
                        }
                    }

                    if (point.winningSide === 1) {
                        // WINNING SIDE 1
                        this.side1Stats.pointsWon++;
                        if (point.winReason === 'A' || point.winReason === 'W') {
                            this.side1Stats[point.winReason]++;
                            if (point.winReason === 'A') this.side1Stats.W++;
                        } else {
                            this.side2Stats[point.winReason]++;
                            if (point.winReason === 'DF') this.side2Stats.U++;
                        }
                        if (point.server?.sideNumber === 1) {
                            this.side1Stats.serveWon++;
                        } else if (point.server?.sideNumber === 2) {
                            this.side1Stats.returnWon++;
                        }
                    } else if (point.winningSide === 2) {
                        // WINNING SIDE 2
                        this.side2Stats.pointsWon++;
                        if (point.winReason === 'A' || point.winReason === 'W') {
                            this.side2Stats[point.winReason]++;
                            if (point.winReason === 'A') this.side2Stats.W++;
                        } else {
                            this.side1Stats[point.winReason]++;
                            if (point.winReason === 'DF') this.side1Stats.U++;
                        }

                        if (point.server?.sideNumber === 2) {
                            this.side2Stats.serveWon++;
                        } else if (point.server?.sideNumber === 1) {
                            this.side2Stats.returnWon++;
                        }
                    }

                    if (this.statsService.isBreakPoint(point, this.scoreFormat)) {
                        point.server.sideNumber === 1
                            ? this.side2Stats.breakPoint++
                            : this.side1Stats.breakPoint++;
                    }

                    if (point.winningSide && !point.winReason) {
                        this.noStats++;
                    }

                    // Rally Length
                    if (point.rallyLength === '0-4') {
                        point.winningSide === 1 ? this.side1Stats.shortRally++ : this.side2Stats.shortRally++;
                    } else if (point.rallyLength === '5-8') {
                        point.winningSide === 1 ? this.side1Stats.mediumRally++ : this.side2Stats.mediumRally++;
                    } else if (point.rallyLength === '9+') {
                        point.winningSide === 1 ? this.side1Stats.longRally++ : this.side2Stats.longRally++;
                    }

                    // SHOTS
                    let secondServeAdded = false;
                    point.shots.forEach((shot, l) => {
                        if (shot.shotType === 'SE' && (shot.shotOutcome === 'O' || shot.shotOutcome === ShotOutcomeEnum.FOOT_FAULT)) {
                            if (point.server?.sideNumber === 1) {
                                if (!secondServeAdded) {
                                    this.side1Stats.secondServePlayed++;
                                    secondServeAdded = true;
                                }

                                if (point.winningSide === 1) {
                                    this.side1Stats.secondServeWon++;
                                } else if (point.winningSide === 2) {
                                    this.side2Stats.secondServeReturnWon++;
                                }
                            } else if (point.server?.sideNumber === 2) {
                                if (!secondServeAdded) {
                                    this.side2Stats.secondServePlayed++;
                                    secondServeAdded = true;
                                }

                                if (point.winningSide === 2) {
                                    this.side2Stats.secondServeWon++;
                                } else if (point.winningSide === 1) {
                                    this.side1Stats.secondServeReturnWon++;
                                }
                            }
                        }

                        if (shot.shotDetail === 'RE' && shot.shotOutcome === 'I') {
                            if (point.server?.sideNumber === 1) {
                                this.side2Stats.returnMade++;
                            } else if (point.server?.sideNumber === 2) {
                                this.side1Stats.returnMade++;
                            }
                        }
                    });

                    if (point.winReason && point.winningSide && !secondServeAdded) {
                        if (point.server?.sideNumber === 1) {
                            if (!secondServeAdded) {
                                this.side1Stats.firstServePlayed++;
                                secondServeAdded = true;
                            }

                            if (point.winningSide === 1) {
                                this.side1Stats.firstServeWon++;
                            } else if (point.winningSide === 2) {
                                this.side2Stats.firstServeReturnWon++;
                            }
                        } else if (point.server?.sideNumber === 2) {
                            this.side2Stats.firstServePlayed++;

                            if (point.winningSide === 2) {
                                this.side2Stats.firstServeWon++;
                            } else if (point.winningSide === 1) {
                                this.side1Stats.firstServeReturnWon++;
                            }
                        }
                    }
                }
            });
        });

    }

    updateStats(filters) {
        this.filters = filters;
        this.getStats();
    }

    private getFilters(point: Point) {
        let rallyLength = false;
        if (!this.filters.rallyLength || point.rallyLength === this.filters.rallyLength) {
            rallyLength = true;
        }

        let serveLocation = false;
        const location = point.shots.find(shot => shot.bounceAt);
        if (!this.filters.serveLocation || (location && location.bounceAt === this.filters.serveLocation)) {
            serveLocation = true;
        }

        let returningSide = false;
        if (!this.filters.returningSide || point.server?.returningSide === this.filters.returningSide) {
            returningSide = true;
        }

        let serverPlayerNumber = false;
        const sideNumber = this.filters.serverPlayerNumber === 1 || this.filters.serverPlayerNumber === 2
            ? 1
            : 2;
        const playerNumber = this.filters.serverPlayerNumber === 1 || this.filters.serverPlayerNumber === 3
            ? 1
            : 2;

        if (!this.filters.serverPlayerNumber || (point.server?.sideNumber === sideNumber && point.server?.playerNumber === playerNumber)) {
            serverPlayerNumber = true;
        }

        const pointsPlayer1 = true;
        const pointsPlayer2 = true;

        return serverPlayerNumber && rallyLength && serveLocation && returningSide && pointsPlayer1 && pointsPlayer2
            ? true
            : false;
    }

    getPercentage(var1: number, var2: number) {
        const percentage = ((var1 / var2) * 100).toFixed(0);
        return var2 ? percentage : 0;
    }

    getDecimal(var1: number, var2: number) {
        return var2 === 0 ? 0 : var1 / var2;
    }

    getProgressBarValue(params: any[], sideNumber: number) {
        const paramsSum = params[0] + params[1];
        return paramsSum === 0 ? 0 : params[sideNumber - 1] / paramsSum;
    }

    convertArrayToRgba(array: any[]) {
        array[3] = '0.1';
        return `rgba(${array.toString()})`;
    }

    downloadCsv() {
        const csvData = this.statsService.getCsvData(this.match);
        this.downloadCsvService.download(csvData, this.match.matchId);
    }
}
